import { useEffect, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const scoreStyle = (s) =>
  s >= 15 ? "bg-red-100 text-red-800" : s >= 8 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
const scoreLabel = (s) => (s >= 15 ? "Hög" : s >= 8 ? "Medel" : "Låg");

export default function Module2() {
  const { user } = useAuth();
  const isManager = ["Kvalitetsansvarig", "Byråledning", "Supervisor"].includes(user.role);
  const [risks, setRisks] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "", description: "", probability: 3, impact: 3, mitigation_strategy: "", trigger_event: "" });

  const load = () => api.get("/risks").then((r) => setRisks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const lastEval = risks?.length
    ? risks.reduce((a, b) => (a.last_evaluated_at > b.last_evaluated_at ? a : b)).last_evaluated_at
    : null;
  const daysLeft = lastEval ? 365 - Math.floor((Date.now() - new Date(lastEval).getTime()) / 86400000) : null;

  const evaluate = async () => {
    try {
      await api.post("/risks/evaluate");
      toast.success("Riskbedömningen har markerats som utvärderad");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/risks", { ...form, probability: Number(form.probability), impact: Number(form.impact) });
      toast.success("Ny risk har lagts till i riskregistret");
      setShowForm(false);
      setForm({ category: "", description: "", probability: 3, impact: 3, mitigation_strategy: "", trigger_event: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!risks) return <div className="text-sm text-slate-500">Laddar...</div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="modul2-view">
      <div className="border-b border-slate-200 pb-4 mb-6 flex flex-wrap gap-3 justify-between items-end">
        <div>
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider">Modul 2 · Reko 220–225</div>
          <h2 className="text-xl font-extrabold text-slate-900">Löpande Riskbedömning</h2>
          <p className="text-sm text-slate-500 mt-1">Proaktivt identifiera och värdera interna och externa risker som hotar kvalitetsmålen.</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`text-xs px-2.5 py-1 rounded border font-semibold ${daysLeft != null && daysLeft <= 30 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200"}`} data-testid="risk-eval-status">
            <RefreshCw className="w-3 h-3 inline mr-1" />
            Senast utvärderad: {lastEval ? new Date(lastEval).toLocaleDateString("sv-SE") : "Aldrig"} (Minst 1ggr/år)
          </span>
          {isManager && (
            <>
              <button data-testid="risk-evaluate-btn" onClick={evaluate} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded shadow">
                Markera som utvärderad
              </button>
              <button data-testid="risk-add-btn" onClick={() => setShowForm(!showForm)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded shadow flex items-center">
                <Plus className="w-3.5 h-3.5 mr-1" />Ny risk
              </button>
            </>
          )}
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" data-testid="risk-form">
          <div className="md:col-span-1">
            <label className="font-semibold text-slate-700 block mb-1">Riskområde *</label>
            <input data-testid="risk-category-input" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
          </div>
          <div className="md:col-span-2">
            <label className="font-semibold text-slate-700 block mb-1">Beskrivning *</label>
            <input data-testid="risk-description-input" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="font-semibold text-slate-700 block mb-1">Sannolikhet (1–5)</label>
            <input data-testid="risk-probability-input" type="number" min="1" max="5" value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="font-semibold text-slate-700 block mb-1">Konsekvens (1–5)</label>
            <input data-testid="risk-impact-input" type="number" min="1" max="5" value={form.impact} onChange={(e) => setForm({ ...form, impact: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="font-semibold text-slate-700 block mb-1">Trigger / Uppföljning</label>
            <input data-testid="risk-trigger-input" value={form.trigger_event} onChange={(e) => setForm({ ...form, trigger_event: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
          </div>
          <div className="md:col-span-3">
            <label className="font-semibold text-slate-700 block mb-1">Fastställda åtgärder *</label>
            <input data-testid="risk-mitigation-input" required value={form.mitigation_strategy} onChange={(e) => setForm({ ...form, mitigation_strategy: e.target.value })} className="w-full p-2 border border-slate-300 rounded" />
          </div>
          <div className="md:col-span-3 flex justify-end space-x-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-slate-100 text-slate-700 font-semibold rounded hover:bg-slate-200">Avbryt</button>
            <button data-testid="risk-submit-btn" type="submit" className="px-3 py-1.5 bg-blue-600 text-white font-bold rounded hover:bg-blue-700">Lägg till risk</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse" data-testid="risk-table">
          <thead>
            <tr className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
              <th className="p-3">Riskområde / Händelse</th>
              <th className="p-3">Sannolikhet (1-5)</th>
              <th className="p-3">Konsekvens (1-5)</th>
              <th className="p-3">Riskvärde</th>
              <th className="p-3">Fastställda Åtgärder (Modul 3)</th>
              <th className="p-3">Trigger / Uppföljning</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {risks.map((r) => (
              <tr key={r.id}>
                <td className="p-3 font-semibold text-slate-800">{r.category}</td>
                <td className="p-3">{r.probability}</td>
                <td className="p-3">{r.impact}</td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded font-bold ${scoreStyle(r.risk_score)}`}>{r.risk_score} ({scoreLabel(r.risk_score)})</span></td>
                <td className="p-3">{r.mitigation_strategy}</td>
                <td className="p-3 text-slate-500">{r.trigger_event}</td>
              </tr>
            ))}
            {risks.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Inga risker registrerade ännu</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
